/**
 * useBankPullTrackers — the trackers whose banks can be pulled here: the
 * enabled trackers that have the `bankPull` capability, in registry order
 * (Voluum, Binom, then any other). Every Tres Commas pull surface (seed
 * pages, sidebar entries, the banks' provider filter) is gated by it.
 * For Media Devoted | Created By Ciro Autiero
 *
 * GET /app-env is asked only once a tracker beyond the defaults CAN pull: while
 * none can (Tres Commas ships dark), the answer is the defaults with no
 * request, so the Voluum/Binom pages make exactly the requests they made before.
 */

import { useMemo } from 'react';
import {
  DEFAULT_ENABLED_TRACKERS,
  TRACKER_IDS,
  trackerCan,
  type TrackerId,
} from '@trafficpulse/shared';

import { useAppEnv } from '@/features/app-env';
import { enabledTrackers } from '@/features/trackers/useEnabledTrackers';

export function bankPullTrackers(enabled: readonly TrackerId[]): readonly TrackerId[] {
  return enabled.filter((id) => trackerCan(id, 'bankPull'));
}

/** Whether any tracker beyond the defaults has the capability at all. */
export function extraTrackerCanPull(): boolean {
  return TRACKER_IDS.some(
    (id) => !DEFAULT_ENABLED_TRACKERS.includes(id) && trackerCan(id, 'bankPull'),
  );
}

export function useBankPullTrackers(): readonly TrackerId[] {
  const needed = extraTrackerCanPull();
  const { data, isError } = useAppEnv({ enabled: needed });
  const reported = needed && !isError ? data?.trackers : undefined;
  return useMemo(() => bankPullTrackers(enabledTrackers(reported)), [reported]);
}
